/* Office Walker — waitlist capture. No-cors POST of the first-party signal set
   to the n8n webhook, inline success state, then redirect to the confirm page.

   Binds to EVERY waitlist form on the page, not just one. It used to look up a
   single element by id, which meant a second capture (the hero) would fall
   through to a native form POST and lose the signup. Mark any form with
   data-ow-wl-form; the old #ow-wl-form id still works so nothing else breaks.

   LOCALISATION, 2026-08-07. The two visitor-facing messages used to be string
   literals in here. Shopify cannot translate a .js asset, so on /de/ a German
   visitor would have submitted the form and been answered in English. They now
   arrive as data-msg-ok / data-msg-err, rendered from locale keys by whichever
   section owns the form. No JS locale object, no dictionary to keep in sync:
   the string is already in the right language by the time JS sees it.

   If an attribute is missing the message is simply skipped rather than falling
   back to English. A silent success still redirects, which is the safe failure.

   DOUBLE LOAD GUARD, 2026-08-08. Two sections each emit a script tag for this
   file, so a page carrying both the hero capture and the CTA capture executed
   the whole thing twice. The browser fetches the file once because the URL is
   identical, but two separate script tags each parse and run it. Harmless for
   correctness, since init() was already idempotent per form via __owWlInit, but
   it is wasted parse and execute on a phone. The guard below makes the second
   run a no-op. The real fix is to emit the tag once from layout/theme.liquid;
   this is the version that does not require touching the layout.

   META MATCH PARAMETERS, 2026-09-10. Sends _fbp and _ow_xid so the server-side
   CAPI Lead can carry fbp and external_id. Both are written by the consent-gated
   Meta base tag, so a visitor who has not granted consent sends empty strings —
   that is correct, not a fault.

   FIRST-TOUCH PERSISTENCE, 2026-09-11. The nine campaign parameters used to be
   read from window.location.search at SUBMIT time. About 8.8k visitors land on
   "/" and convert on "/pages/join-the-waitlist", and a query string does not
   survive that navigation — so utm_*, gclid, fbclid, ttclid and msclkid were
   arriving empty for almost everyone. They are now captured on every page load
   into sessionStorage and read back at submit, live URL winning when present.
   Latest touch wins inside a session. This is what makes campaign attribution
   work at all, and it matters more from October when Google Ads starts.

   fbc, 2026-09-11. Sent from the _fbc cookie, which the Meta pixel writes from
   fbclid on landing and which survives navigation. Falls back to constructing
   fb.1.<observed-timestamp>.<fbclid> from the stashed value when the cookie is
   absent — the timestamp is recorded when the fbclid was first SEEN, which is
   what Meta asks for. Subdomain index is 1 because the pixel is on the apex.
   fbclid is case-sensitive and is never transformed anywhere in this file.

   EMAIL VALIDATION, 2026-09-11. input[type=email] accepts "x@gmail.c" because
   HTML5 does not check TLD length, which is how a bad address reached Brevo and
   triggered a 400. A stricter check now blocks submission and shows
   data-msg-invalid (falling back to data-msg-err if that key is not rendered
   yet). */
(function () {
  'use strict';

  if (window.__owWaitlistLoaded) { return; }
  window.__owWaitlistLoaded = true;

  var TRACK_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
                    'utm_term', 'gclid', 'fbclid', 'ttclid', 'msclkid'];
  var STORE_KEY = 'ow_touch';

  /* Runs on EVERY page load, before any navigation can strip the query string.
     Wrapped whole: sessionStorage throws in some private modes and no failure
     here may ever affect the form. */
  function stashTouch() {
    try {
      var q = new URLSearchParams(window.location.search);
      var found = {};
      var any = false;
      TRACK_KEYS.forEach(function (k) {
        var v = q.get(k);
        if (v) { found[k] = v; any = true; }
      });
      if (!any) { return; }
      var prev = {};
      try { prev = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '{}') || {}; }
      catch (e) { prev = {}; }
      /* Record when fbclid was first observed — Meta wants the time of
         observation in the fbc timestamp, not the time of conversion. */
      if (found.fbclid && prev.fbclid !== found.fbclid) {
        prev.fbclid_ts = Date.now();
      }
      TRACK_KEYS.forEach(function (k) { if (found[k]) { prev[k] = found[k]; } });
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(prev));
    } catch (e) {}
  }

  function readTouch() {
    try { return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '{}') || {}; }
    catch (e) { return {}; }
  }

  /* Rejects x@gmail.c — requires a TLD of at least two letters. Deliberately
     not RFC-complete; it only needs to catch the typos that reach Brevo. */
  var EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[A-Za-z]{2,}$/;

  function init(form) {
    if (!form || form.__owWlInit) return;
    form.__owWlInit = true;

    var hasAction = form.getAttribute('data-has-action') === '1';
    var ks = form.getAttribute('data-ks-url');
    var redirect = form.getAttribute('data-redirect') || ks;
    var msgOk = form.getAttribute('data-msg-ok') || '';
    var msgErr = form.getAttribute('data-msg-err') || '';
    var msgInvalid = form.getAttribute('data-msg-invalid') || msgErr;
    var email = form.querySelector('input[type="email"]');
    var row = form.querySelector('.ow-wl__row');
    var note = form.querySelector('.ow-wl__note');
    var msg = form.querySelector('[data-ow-msg]');
    var submitBtn = form.querySelector('.ow-wl__submit');
    var submitting = false;

    function showMsg(text, isError) {
      if (!msg || !text) return;
      msg.textContent = text;
      msg.classList.toggle('is-error', !!isError);
      msg.hidden = false;
    }
    function goNext() { if (redirect) { window.location.href = redirect; } }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitting) return;
      if (email && !form.checkValidity()) { form.reportValidity(); return; }
      var action = form.getAttribute('action');
      var val = email ? email.value.trim() : '';

      /* Stricter than the browser. Blocks and prompts rather than advancing to
         the confirm page with an address that can never receive the DOI mail. */
      if (val && !EMAIL_RE.test(val)) {
        showMsg(msgInvalid, true);
        if (email) { email.focus(); email.select && email.select(); }
        return;
      }

      if (!(hasAction && action && val)) {
        if (redirect) { goNext(); } else if (email) { email.focus(); }
        return;
      }

      submitting = true;
      if (submitBtn) { submitBtn.disabled = true; }

      var hp = form.querySelector('input[name="email_address_check"]');
      var qs;
      try { qs = new URLSearchParams(window.location.search); } catch (e) { qs = null; }
      var touch = readTouch();
      /* Live URL wins when the parameter is present on this page; otherwise
         fall back to what was captured on the landing page this session. */
      function qp(name) {
        var live = qs ? (qs.get(name) || '') : '';
        if (live) { return live; }
        return touch[name] || '';
      }
      /* Cookie reads happen HERE, at submit time, never at init. The homepage
         hero rotator destroys and re-creates the form, so anything captured at
         init goes stale. Any failure returns '' so the POST still goes out:
         encodeURIComponent(undefined) would serialise the literal "undefined",
         which n8n's `|| ""` coercion cannot rescue. */
      function ck(name) {
        try {
          var parts = ('; ' + document.cookie).split('; ' + name + '=');
          if (parts.length < 2) { return ''; }
          var raw = parts.pop().split(';').shift();
          if (!raw) { return ''; }
          try { return decodeURIComponent(raw); } catch (e) { return raw; }
        } catch (e) { return raw_fallback(raw); }
      }
      function raw_fallback(v) { return v || ''; }
      /* Prefer the cookie the pixel wrote. Build it only if the cookie is gone
         but we still hold the click ID. Index 1 = apex domain. */
      function fbcValue() {
        try {
          var c = ck('_fbc');
          if (c) { return c; }
          var id = qp('fbclid');
          if (!id) { return ''; }
          var ts = touch.fbclid_ts || Date.now();
          return 'fb.1.' + ts + '.' + id;
        } catch (e) { return ''; }
      }
      var tz = '';
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) {}
      var ua = navigator.userAgent || '';
      var isIpad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
      var device = 'other';
      if (/iPhone|iPod/i.test(ua) || isIpad) { device = 'ios'; }
      else if (/Android/i.test(ua)) { device = 'android'; }
      else if (/Macintosh|Mac OS X/i.test(ua)) { device = 'desktop-mac'; }
      else if (/Windows|Win32|Win64/i.test(ua)) { device = 'desktop-windows'; }
      var fields = {
        email: val,
        email_address_check: hp ? hp.value.trim() : '',
        language: navigator.language || '',
        timezone: tz,
        source_page: window.location.pathname || '',
        capture: form.getAttribute('data-capture') || '',
        referrer: document.referrer || '',
        utm_source: qp('utm_source'),
        utm_medium: qp('utm_medium'),
        utm_campaign: qp('utm_campaign'),
        utm_content: qp('utm_content'),
        utm_term: qp('utm_term'),
        gclid: qp('gclid'),
        fbclid: qp('fbclid'),
        ttclid: qp('ttclid'),
        msclkid: qp('msclkid'),
        fbp: ck('_fbp'),
        fbc: fbcValue(),
        ow_xid: ck('_ow_xid'),
        device: device,
        shopify_country: form.getAttribute('data-country') || ''
      };
      var body = Object.keys(fields).map(function (k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(fields[k]);
      }).join('&');
      fetch(action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body,
        mode: 'no-cors'
      }).then(function () {
        if (row) { row.hidden = true; }
        if (note) { note.hidden = true; }
        showMsg(msgOk, false);
        window.setTimeout(goNext, 2200);
      }).catch(function () {
        submitting = false;
        if (submitBtn) { submitBtn.disabled = false; }
        showMsg(msgErr, true);
      });
    });
  }

  function boot() {
    stashTouch();
    document.querySelectorAll('form[data-ow-wl-form]').forEach(init);
    init(document.getElementById('ow-wl-form'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();